import { useState } from 'react';
import { PenLine } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { PaveSignature } from '../ui/PaveSignature';
import { BoutonMint, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import type { SeanceAffichee } from '../../lib/useEmargement';

/**
 * Signature du formateur sur la tablette (CDC §6.7).
 *
 * « Signature apprenant et formateur par demi-journée » : en présentiel, le
 * formateur trace sa signature comme les apprenants, et elle est reproduite au
 * PV. L'API refuse la signature sans tracé ; ici, le bouton attend le tracé et
 * la déclaration.
 *
 * Le pavé est remonté par une `key` propre à la séance : une fenêtre rouverte
 * sur une autre demi-journée ne garde pas le tracé de la précédente.
 */
export function ModaleSignatureFormateur({
  seance,
  ouverte,
  enCours,
  erreur,
  onFermer,
  onSigner,
}: {
  seance: SeanceAffichee;
  ouverte: boolean;
  enCours: boolean;
  erreur: string | null;
  onFermer: () => void;
  onSigner: (trace: string) => void;
}): JSX.Element {
  const [trace, setTrace] = useState<string | null>(null);
  const [atteste, setAtteste] = useState(false);

  const fermer = (): void => {
    setTrace(null);
    setAtteste(false);
    onFermer();
  };

  return (
    <Modale
      ouverte={ouverte}
      titre="Signature du formateur"
      sousTitre={`${seance.libelle} · ${seance.demiJourneeLibelle} · ${seance.heureDebut} – ${seance.heureFin}`}
      onFermer={fermer}
    >
      <div className="space-y-4 p-4 sm:p-6">
        <PaveSignature key={seance.id} onChanger={setTrace} desactive={enCours} hauteur="h-48" />

        <label className="flex min-h-[44px] items-start gap-3 text-sm text-codialis-navy">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 shrink-0 rounded [@media(pointer:coarse)]:h-5 [@media(pointer:coarse)]:w-5"
            checked={atteste}
            onChange={(evenement) => setAtteste(evenement.target.checked)}
          />
          <span>
            J’atteste avoir animé cette demi-journée. Ma signature est horodatée et reproduite sur
            la feuille d’émargement archivée.
          </span>
        </label>

        {erreur && <MessageErreur message={erreur} />}

        <div className="flex flex-wrap gap-2">
          <BoutonMint onClick={() => trace && onSigner(trace)} disabled={!trace || !atteste || enCours}>
            <PenLine className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {enCours ? 'Signature…' : 'Signer la feuille'}
          </BoutonMint>
          <BoutonSecondaire onClick={fermer} disabled={enCours}>
            Annuler
          </BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}
